import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { statisticsApi } from '../services/api';
import { StatisticsResponse } from '../types';
import StatCard from '../components/stats/StatCard';
import CharacterPlayCard from '../components/stats/CharacterPlayCard';

const StatsPage: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [statistics, setStatistics] = useState<StatisticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await statisticsApi.getUserStatistics(user.id);
        setStatistics(data);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
        setError('Failed to load statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Statistics</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Statistics Available</h2>
          <p className="text-gray-600">Play some games to see your statistics!</p>
        </div>
      </div>
    );
  }

  const { statistics: stats, derivedMetrics } = statistics;

  // Sort characters by play count for display
  const sortedCharacters = Object.entries(stats.characterPlayCounts)
    .map(([characterId, playCount]) => ({
      characterId,
      playCount,
      percentage: stats.totalGames > 0 ? (playCount / stats.totalGames) * 100 : 0,
    }))
    .sort((a, b) => b.playCount - a.playCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Player Statistics</h1>
          <p className="mt-2 text-gray-600">
            Your gameplay performance and achievements
          </p>
        </div>

        {/* Overview Stats */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Game Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Games"
              value={stats.totalGames}
              icon="🎮"
              color="blue"
            />
            <StatCard
              title="Win Rate"
              value={`${derivedMetrics.winRatePercentage.toFixed(1)}%`}
              subtitle={`${stats.wins}W / ${stats.losses}L`}
              icon="🏆"
              color="green"
            />
            <StatCard
              title="Current Streak"
              value={stats.currentWinStreak}
              subtitle={`Best: ${stats.longestWinStreak}`}
              icon="🔥"
              color="yellow"
            />
            <StatCard
              title="Avg Game Time"
              value={`${stats.averageGameDuration.toFixed(1)}m`}
              icon="⏱️"
              color="purple"
            />
          </div>
        </div>

        {/* Combat Stats */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Combat Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Damage"
              value={stats.totalDamageDone}
              subtitle={`${derivedMetrics.averageDamagePerGame.toFixed(1)} per game`}
              icon="⚔️"
              color="red"
            />
            <StatCard
              title="Health Healed"
              value={stats.totalHealthHealed}
              subtitle={`${derivedMetrics.averageHealingPerGame.toFixed(1)} per game`}
              icon="💚"
              color="green"
            />
            <StatCard
              title="K/D Ratio"
              value={derivedMetrics.killDeathRatio.toFixed(2)}
              subtitle={`${stats.totalKills}K / ${stats.totalDeaths}D`}
              icon="💀"
              color="gray"
            />
            <StatCard
              title="Movement"
              value={stats.totalPacesMoved}
              subtitle={`${derivedMetrics.averageMovementPerGame.toFixed(1)} per game`}
              icon="👟"
              color="blue"
            />
          </div>
        </div>

        {/* Special Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Special Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Knockdowns"
              value={stats.totalPlayersKnockedDown}
              icon="🥊"
              color="red"
            />
            <StatCard
              title="Disarms"
              value={stats.totalPlayersDisarmed}
              icon="🛡️"
              color="yellow"
            />
            <StatCard
              title="Pushes"
              value={stats.totalPlayersPushed}
              icon="👐"
              color="blue"
            />
            <StatCard
              title="Abilities Used"
              value={stats.totalAbilitiesUsed}
              subtitle={stats.favoriteAbility}
              icon="✨"
              color="purple"
            />
          </div>
        </div>

        {/* Critical Hits & Fails */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dice Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StatCard
              title="Critical Hits"
              value={stats.criticalHits}
              subtitle="Roll ≥ 18"
              icon="🎯"
              color="green"
            />
            <StatCard
              title="Critical Fails"
              value={stats.criticalFails}
              subtitle="Roll ≤ 3"
              icon="💥"
              color="red"
            />
          </div>
        </div>

        {/* Character Statistics */}
        {sortedCharacters.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Character Usage</h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedCharacters.map((char) => (
                  <CharacterPlayCard
                    key={char.characterId}
                    characterId={char.characterId}
                    playCount={char.playCount}
                    percentage={char.percentage}
                    isMostPlayed={char.characterId === stats.mostPlayedCharacter}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>Statistics are updated in real-time during gameplay</p>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
